"use client";

import type { StatsSummaryComponent } from "../../types";
import { ArrowUpRight, DollarSign, PoundSterling, JapaneseYen } from "lucide-react";
import { StatsCard } from "../../components/home/cards/StatsCard";
import { RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";

/** Key indicators summary renderer: interest rates, exchange rates and surveys. */
export function StatsSummaryRenderer({ component }: { component: StatsSummaryComponent }) {
  const locale = useLocale();
  const s = component.content;
  const CURRENCY_FLAGS: Record<string, string> = { USD: "us", GBP: "gb", JPY: "jp" };
  const getCurrencyIcon = (code: string) => {
    switch (code) {
      case "USD":
        return <DollarSign className="h-4 w-4" />;
      case "GBP":
        return <PoundSterling className="h-4 w-4" />;
      case "JPY":
        return <JapaneseYen className="h-4 w-4" />;
      default:
        return null;
    }
  };
  return (
    <section className="mt-8 mx-[calc(50%-50vw)] bg-[#70C3F3] py-10">
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 md:px-8 md:grid-cols-3">
        <StatsCard
          title={t(locale, "interestRate")}
          footer={
            <RuntimeLink href="/chiffres-cles">
              {t(locale, "allKeyFigures")} <span className="text-[#FFC105]">→</span>
            </RuntimeLink>
          }
        >
          <div className="space-y-3">
            {s.interestRates.map((r) => (
              <div key={r.label} className="flex items-center justify-between pb-1 md:pb-2 text-base xl:text-lg">
                <span>{r.label}</span>
                <span className="font-semibold">{r.value}</span>
              </div>
            ))}
          </div>
        </StatsCard>
        <StatsCard
          title={t(locale, "exchangeRate")}
          footer={
            <RuntimeLink href="/changes">
              {t(locale, "allFxRates")} <span className="text-[#FFC105]">→</span>
            </RuntimeLink>
          }
        >
          <div className="space-y-3">
            {s.exchangeRates.map((e) => {
              const flagIso = CURRENCY_FLAGS[e.code] || "";
              return (
                <div key={e.code} className="flex items-center justify-between pb-1 md:pb-2 text-base xl:text-lg">
                  <span className="flex items-center gap-2">
                    {flagIso && (
                      <span
                        className={`fi fi-${flagIso} text-[13px] xl:text-[15px]`}
                        style={{
                          display: "inline-flex",
                          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.18)",
                        }}
                      ></span>
                    )}
                    {e.code}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    {getCurrencyIcon(e.code)}
                    {e.value}
                  </span>
                </div>
              );
            })}
          </div>
        </StatsCard>
        <StatsCard
          title={t(locale, "surveys")}
          footer={
            <RuntimeLink href="/chiffres">
              {t(locale, "allFigures")} <span className="text-[#FFC105]">→</span>
            </RuntimeLink>
          }
        >
          <div className="space-y-4">
            {s.surveys.map((sv) => (
              <div key={sv.label} className="flex items-center justify-between pb-1 md:pb-2 text-base xl:text-lg">
                <span>{sv.label}</span>
                <span className="inline-flex items-center gap-1 text-emerald-600">{sv.value}
                  <ArrowUpRight className="h-4 w-4 " />
                </span>
              </div>
            ))}
          </div>
        </StatsCard>
      </div>
    </section>
  );
}


